import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, NEVER, Observable, of } from 'rxjs';
import { ShowIfHttpError } from '@certemy/common';
import { HttpService } from 'services/http/http.service';
import { createFileUploadHeaders } from 'utils/helpers';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ImageCropperDefaultConfiguration, skipModalClose, SmartModalService } from '@certemy/ui-components';
import { UploadImageModalComponent } from 'components/upload-image-modal/upload-image-modal.component';
import { blobToFile } from 'components/upload-image-modal/upload-image-modal.helpers';
import {
  EntityForImageUpdate,
  EntityImageConfig,
  UPLOAD_IMAGE_ACTION,
} from 'components/upload-image-modal/upload-image-modal.constants';
@Injectable()
export class UploadImageModalService {
  constructor(private http: HttpService, private smartModalService: SmartModalService) {}

  @ShowIfHttpError('An error occurred while updating image. Please, try again')
  updateEntityImage(entityImageConfig: EntityImageConfig, entityConfig: { type: EntityForImageUpdate; id: number }) {
    return this.http.post(`/avatar/${entityConfig.type}/${entityConfig.id}`, entityImageConfig);
  }

  @ShowIfHttpError('Cannot upload logo file')
  uploadFile(file: File, fileName = '', params = {}): Observable<string> {
    const formData = new FormData();
    const options = { ...createFileUploadHeaders(), params };
    formData.append('file', file, fileName);

    return this.http.post(`/file`, formData, options).pipe(map(result => result[0].link));
  }

  openUploadModal(
    modalConfig: {
      defaultConfiguration: ImageCropperDefaultConfiguration;
      entityConfig: {
        type: EntityForImageUpdate;
        id: number;
      };
      resetScreenMessage: string;
      manualUpdate?: boolean;
    },
    imageLoading$: BehaviorSubject<boolean>,
  ) {
    return this.smartModalService
      .open(UploadImageModalComponent, {
        defaultConfiguration: modalConfig.defaultConfiguration,
        resetScreenMessage: modalConfig.resetScreenMessage,
        entity: modalConfig.entityConfig.type,
      })
      .pipe(
        skipModalClose(),
        switchMap(({ payload }) => {
          if (payload.action === UPLOAD_IMAGE_ACTION.RESET) {
            return of(null);
          } else {
            const imageConfig = payload.config;
            const newLogo = blobToFile(imageConfig.blob, 'logo.png');
            imageLoading$.next(true);
            const originalImage$ = (typeof imageConfig.originalImageFile === 'string')
              ? of(imageConfig.originalImageFile)
              : this.uploadImage(imageConfig.originalImageFile, 'originalLogo.png');

            return forkJoin([
              this.uploadImage(newLogo as File, 'logo.png'),
              originalImage$,
              of(imageConfig.configuration),
            ]);
          }
        }),
        catchError(() => {
          imageLoading$.next(false);
          return NEVER;
        }),
        switchMap(config => {
          const [imageFile, originalImageFile, imageConfiguration] = config ? config : [null, null, null];
          const imageConfig = {
            imageFile,
            originalImageFile,
            imageConfiguration,
          };
          if (modalConfig.entityConfig.id && !modalConfig.manualUpdate) {
            return forkJoin([
              of(imageConfig),
              this.updateEntityImage(
                {
                  imageFile,
                  originalImageFile,
                  imageConfiguration,
                },
                modalConfig.entityConfig,
              ),
            ]);
          } else {
            return forkJoin([of(imageConfig)]);
          }
        }),
        catchError(() => {
          imageLoading$.next(false);
          return NEVER;
        }),
      );
  }

  uploadImage(image: File, fileName: string) {
    return this.uploadFile(image, fileName, { skipLoaderLock: true });
  }
}
